///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2024 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Spinner from "../icons/Spinner";
import messages from "../../helpers/messages";
import OutlinedInput from "../form-controls/OutlinedInput";
import { updateStrategicQuantile } from "../../api/strategicQuantile";
import Alert from "../Alert";
import SystemErrorModal from "../SystemErrorModal";
import { fetchStrategicQuantile } from "../../slices/strategicQuantileSlice";

export const StrategicQuantile = () => {
  const dispatch = useDispatch();
  const { strategicQuantile } = useSelector(state => state.strategicQuantile);

  const [minimumValue, setMinimumValue] = useState(null);
  const [maximumValue, setMaximumValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [failureModal, setFailureModal] = useState(false);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (strategicQuantile) {
      setMinimumValue(strategicQuantile?.minimumValue);
      setMaximumValue(strategicQuantile?.maximumValue);
    }
  }, [strategicQuantile]);

  useEffect(() => {
    if (minimumValue < maximumValue) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [minimumValue, maximumValue]);

  const handleStrategicQuantileChange = event => {
    if (!event.target.validity.patternMismatch) {
      if (event.target.name === "minimumValue") {
        setMinimumValue(
          event.target.value
            ? parseInt(event.target.value, 10)
            : event.target.value
        );
      } else {
        setMaximumValue(
          event.target.value
            ? parseInt(event.target.value, 10)
            : event.target.value
        );
      }
    }
  };

  const saveChanges = () => {
    setFailureModal(false);
    setSaving(true);
    updateStrategicQuantile(minimumValue, maximumValue)
      .then(() => {
        setSaving(false);
        setSuccessModal(true);
      })
      .catch(() => {
        setSaving(false);
        setFailureModal(true);
      });
  };

  const handleSuccessModalClose = () => {
    setMinimumValue(null);
    setMaximumValue(null);
    setSuccessModal(false);
    dispatch(fetchStrategicQuantile());
  };

  return (
    <>
      <Alert
        title={messages.success.defaultMessage}
        message={messages.manageUsersSuccessMessage.defaultMessage}
        open={successModal}
        handleClose={handleSuccessModalClose}
        large={true}
      />
      <SystemErrorModal
        open={failureModal}
        handleTryAgain={saveChanges}
        closeModal={() => setFailureModal(false)}
        handleExit={() => setFailureModal(false)}
      />
      <div className="py-16 grid section-grid">
        <div
          data-testid="minimum-value-container"
          className="grid__cell one-half pl-24 pr-4"
        >
          <OutlinedInput
            data-testid="minimum-value"
            name="minimumValue"
            value={minimumValue}
            label={messages.minimumValue.defaultMessage}
            pattern="^([0-9]|[1-9][0-9]|100)$"
            onChange={handleStrategicQuantileChange}
            unit="%"
          />
        </div>
        <div
          data-testid="maximum-value-container"
          className="grid__cell one-half pl-4 pr-24"
        >
          <OutlinedInput
            data-testid="maximum-value"
            name="maximumValue"
            value={maximumValue}
            label={messages.maximumValue.defaultMessage}
            pattern="^([0-9]|[1-9][0-9]|100)$"
            onChange={handleStrategicQuantileChange}
            unit="%"
          />
        </div>
        <button
          className="mr-24 primary-button fr univers-67-bold-cond mt-12 mb-24 block"
          style={{ minWidth: "120px" }}
          disabled={!isValid}
          onClick={saveChanges}
          data-testid="save"
        >
          {saving && (
            <Spinner
              className="spinner"
              fill="#ffffff"
              height="16px"
              width="16px"
            />
          )}
          {!saving && messages.save.defaultMessage}
        </button>
      </div>
    </>
  );
};

export default StrategicQuantile;
